import { serverSidePagePrepare } from "@lib/hooks"
import { observer } from "mobx-react"
import {
  DocumentTemplate,
  Header,
  Page,
  PureLogo,
  useStore,
} from "@features/common"
import { RouterLink } from "@lib/routing"
import { scrollToTop } from "@lib/scroll"
import { IconHamburger } from "@lib/icons"
import { dateFormater } from "@lib/date"
import {
  DesktopTableOfContents,
  LegalSectionView,
  PreviousVersions,
  TouchTableOfContents,
} from "@features/terms"
import { PrivacyLogo } from "@features/privacy"
import { redirect } from "@lib/isomorphic"

export const getServerSideProps = serverSidePagePrepare({
  callback: async ({ Models, ctx }) => {
    const { effective_date } = ctx.query
    const queryDate = Array.isArray(effective_date)
      ? effective_date[0]
      : effective_date

    try {
      const countryCode = await Models.GlobalModel.getClientCountryCode(ctx)
      await Models.LegalModel.getBiometricDataPrivacyPolicyPageData(
        queryDate,
        countryCode,
      )
    } catch {
      redirect(`/${Models.GlobalModel.lang}/not-found`, ctx)
    }
  },
})

const BiometricDataPrivacyPolicyPage = observer(
  function BiometricDataPrivacyPolicyPage() {
    const {
      GlobalModel: { openMenu },
      LegalModel: {
        biometricDataPrivacyPolicySections,
        biometricDataPrivacyPolicyTitle,
        biometricDataPrivacyPolicyEffectiveDate,
        biometricDataPrivacyPolicyVersions,
      },
    } = useStore()

    return (
      <Page
        meta={{
          title: biometricDataPrivacyPolicyTitle,
        }}
        header={
          <Header
            theme="dark"
            logo={
              <RouterLink
                uri="/legal/biometric-data-privacy-policy"
                onClick={() => scrollToTop()}
                data-test-id="header__pure_logo"
                aria-label="root"
                className="mt-4 mb-3 tablet-m:mt-[20px] tablet-m:mb-[18px]"
              >
                <div className="shrink-0 flex items-stretch h-4 tablet-m:h-7">
                  <PureLogo className="shrink-0 block h-full w-auto tablet:h-full text-white pr-2 tablet-m:pr-4" />
                  <PrivacyLogo />
                </div>
              </RouterLink>
            }
            languageSwitcher={null}
            navigationOpener={
              <button
                onClick={openMenu}
                data-test-id="header__menu_button"
                aria-label="menu"
              >
                <IconHamburger className="w-8 h-8 text-white" />
              </button>
            }
          />
        }
        seoH1={biometricDataPrivacyPolicyTitle}
      >
        <div className="container pt-3 pb-15 laptop:pt-8 laptop:pb-20 legal-document">
          {biometricDataPrivacyPolicySections.length > 1 ? (
            <TouchTableOfContents
              tableOfContents={biometricDataPrivacyPolicySections}
            />
          ) : null}
          <DocumentTemplate
            sidebar={
              biometricDataPrivacyPolicySections.length > 1 ? (
                <DesktopTableOfContents
                  tableOfContents={biometricDataPrivacyPolicySections}
                />
              ) : null
            }
          >
            <section className="space-y-10" id="scrollContainerElement">
              {biometricDataPrivacyPolicySections.map((i) => (
                <LegalSectionView data={i} key={i.id} id={i.id} />
              ))}
              <div>
                <div className="pb-3">
                  <strong>Effective date:</strong>{" "}
                  {biometricDataPrivacyPolicyEffectiveDate
                    ? dateFormater(
                        new Date(biometricDataPrivacyPolicyEffectiveDate),
                        "MMMM yyyy",
                        "en",
                      )
                    : ""}
                </div>
                <PreviousVersions
                  versions={biometricDataPrivacyPolicyVersions}
                />
              </div>
            </section>
          </DocumentTemplate>
        </div>
      </Page>
    )
  },
)

export default BiometricDataPrivacyPolicyPage
